// src/features/rooms/pages/RoomDetailsPage/components/RoomFilesSection.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import SelectFileDialog from '../../../../files/components/SelectFileDialog';
import FileSourceButtons from '../../../../files/components/FileSourceButtons';
import { useRoomFiles } from '../../../hooks/useRoomFiles';
import { useRooms } from '../../../hooks/useRooms';
import { FilePreviewSidebar } from '../../../../files/components/FileExplorer/modules/PreviewSidebar';
import { useFilePermissions, useRoomPermissions } from '../../../../auth/hooks/usePermissions';
import { FileDropZone, useFilePicker, useUploadQueue, UploadProgressList } from '../../../../files/upload';

export default function RoomFilesSection({ room, onRoomUpdate }) {
  const { t } = useTranslation(['rooms', 'files', 'common']);
  const { canUpload: canUploadFiles, canRead: canReadFiles } = useFilePermissions();
  const { canUpdate: canUpdateRoom } = useRoomPermissions();
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [browseFolder, setBrowseFolder] = useState(null);

  // Combined permission for modifying room drawing (uploads go to the room's
  // folder, which is looked up first, and picking one browses Filer)
  const canModifyDrawing = canUpdateRoom && canUploadFiles && canReadFiles;

  const {
    error,
    clearError,
    uploadRoomFile,
    ensureRoomFolder,
    ensureRoomsMetaFolder,
  } = useRoomFiles();

  const { updateRoom, loading: updateLoading } = useRooms();

  const setDrawing = useCallback(async (fileId) => {
    if (!room?.room_id) return;
    await updateRoom(room.room_id, { room_drawing_file_id: fileId });
    onRoomUpdate?.();
  }, [room, updateRoom, onRoomUpdate]);

  // A new drawing goes to the room's folder (created on the first upload) and
  // replaces the current one
  const uploads = useUploadQueue({
    upload: (file, { onUploadProgress }) => uploadRoomFile(file, room?.name, { onUploadProgress }),
    onUploaded: (result) => setDrawing(result.data?.data?.file_id),
  });
  const addFile = (files) => uploads.enqueue(files.slice(0, 1));
  const picker = useFilePicker({ onFiles: addFile, multiple: false });

  // Picking a file already in Filer: start in the room's folder, without creating it
  const handleOpenSelectDialog = useCallback(async () => {
    try {
      const roomsId = await ensureRoomsMetaFolder({ create: false });
      const roomId = roomsId ? await ensureRoomFolder(room.name, { create: false }) : null;
      const trail = [{ id: null, name: t('files:root') }];
      if (roomsId) trail.push({ id: roomsId, name: t('files:i18n_folder_names.rooms') });
      if (roomId) trail.push({ id: roomId, name: room.name });
      setBrowseFolder({ folderId: roomId || roomsId || null, breadcrumbs: trail });
    } catch {
      setBrowseFolder(null);
    }
    setSelectDialogOpen(true);
  }, [ensureRoomsMetaFolder, ensureRoomFolder, room, t]);

  const handleFileSelect = useCallback((file) => setDrawing(file?.file_id || file), [setDrawing]);

  // Removes the drawing from the room; the file stays in Filer
  const handleRemoveDrawing = useCallback(() => setDrawing(null), [setDrawing]);

  return (
    <>
      <Card sx={{ minWidth: "250px" }}>
        <FileDropZone
          onFiles={addFile}
          multiple={false}
          disabled={!canModifyDrawing}
          label={t('rooms:drop_drawing')}
          sx={{ height: '100%' }}
        >
          <CardContent sx={{ minHeight: 600, display: "flex", flexDirection: "column" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={2}>
              <Typography variant="h6">
                {t('rooms:room_drawing')}
              </Typography>
              {canModifyDrawing && (
                <>
                  <FileSourceButtons
                    onChoose={handleOpenSelectDialog}
                    onUpload={picker.open}
                    uploadLabel={!room.room_drawing ? t('rooms:actions.upload_drawing') : t('rooms:actions.replace_drawing')}
                    disabled={updateLoading || uploads.busy}
                  />
                  {picker.input}
                </>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />

            {error && (
              <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {updateLoading && !room.room_drawing ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress />
              </Box>
            ) : !room.room_drawing ? (
              <Alert severity="info">
                {t('rooms:messages.no_drawing_attached')}
                {canModifyDrawing && <Box component="span" display="block">{t('rooms:messages.drop_drawing_hint')}</Box>}
              </Alert>
            ) : (
              <FilePreviewSidebar
                file={room.room_drawing}
                width={"100%"}
                onDelete={canModifyDrawing ? handleRemoveDrawing : undefined}
                deleteLabel={t('rooms:actions.remove_drawing')}
                sx={{ aspectRatio: '210 / 297' }}
              />
            )}
          </CardContent>
        </FileDropZone>
      </Card>

      <SelectFileDialog
        open={selectDialogOpen}
        onClose={() => setSelectDialogOpen(false)}
        onSelectFile={handleFileSelect}
        startingFolderId={browseFolder?.folderId ?? null}
        initialBreadcrumbs={browseFolder?.breadcrumbs ?? null}
        title={t('rooms:actions.choose_drawing')}
        allowUpload={true}
      />

      <UploadProgressList
        variant="floating"
        items={uploads.items}
        onDismiss={uploads.dismiss}
        onClose={uploads.clearFinished}
      />
    </>
  );
}
